/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { isNotNil } from 'es-toolkit';

import { HuggingFaceUrls } from '@fai/constants/shared-urls';
import i18n from '@fai/friendli-suite/features/i18n';
import type { ChatModel } from '@fai/friendli-suite/features/products/serverless-endpoints/chat-mock-data/model';
import { ServerlessModelType } from '@fai/friendli-suite/features/types/serverless-endpoints';
import { serverlessModels } from '@fai/ssot/model-list';
import { assert } from '@fai/util/types';

import {
  chatWithFriend,
  explainNpAndP,
  funnyMathProfessor,
  explainingTransformer,
  pythonFunction,
  poemGenerator,
  typescriptFunction,
  popularMovies,
  milesToKilometers,
  weirdestFood,
  compoundInterest,
  mortgage,
  bestSneakers,
  quadraticExpression,
  exchangeRate,
  generateAndCalculate,
  addFibonacciSequence,
  addNumbers,
  factorialCode,
  bubbleSort,
  productPrimes,
  friendliModelOptimizer,
  friendliTCache,
  friendliAgents,
  draftCoverLetter,
  createRecipe,
  computeDerivativeOfFunction,
  solvePythonProblem,
  statisticProblems,
  graphPlot,
  dateCalculation,
  threeDimensionPlot,
  averageShots,
  llamaPaperRelease,
  monteCarlo,
  independenceDay,
} from './examples';
import metaLogo from './images/logos/meta.webp';
import mixtralLogo from './images/logos/mixtral.webp';
import meta_llama_3_1_70b_instruct from './images/thumbnails/llama-3-1-70b-instruct.webp';
import meta_llama_3_1_8b_instruct from './images/thumbnails/llama-3-1-8b-instruct.webp';
import mixtral_8x7b_instruct_v0_1 from './images/thumbnails/mixtral-8x7b-instruct-v0-1.webp';

const commonMaxTokenThreshold = 4096;
const commonInitialValues = {
  prompt: '',
  system_prompt: '',
  tools: [],
  min_tokens: 0,
  max_tokens: 512,
  temperature: 1,
  top_p: 0.8,
  frequency_penalty: 0,
  stop: [],
  resume_generation: true,
};

const getBasicInfo = (modelId: string) => {
  const basicInfo = serverlessModels
    .map((model) => ({
      id: model['ID'],
      name: model['Model name'],
      price: model['Pricing'],
    }))
    .find(({ id }) => id === modelId);

  assert(isNotNil(basicInfo));

  return basicInfo;
};

export const mockedChatModels: ChatModel[] = [
  {
    ...getBasicInfo('meta-llama-3.1-8b-instruct'),
    thumbnail: meta_llama_3_1_8b_instruct,
    companyLogo: metaLogo,
    type: ServerlessModelType.Chat,

    maxTokenThreshold: commonMaxTokenThreshold,
    initialValues: commonInitialValues,

    info: {
      id: 'meta-llama-3.1-8b-instruct',
      license: {
        label: i18n.t('shared.license'),
        link: 'https://llama.meta.com/llama3_1/license',
      },
      policy: {
        label: i18n.t('shared.policy'),
        link: 'https://llama.meta.com/llama3_1/use-policy',
      },
      modelCard: {
        label: 'Meta-Llama-3.1-8B-Instruct',
        link: `${HuggingFaceUrls.Default}/meta-llama/Meta-Llama-3.1-8B-Instruct`,
      },
    },

    examples: [
      draftCoverLetter,
      computeDerivativeOfFunction,
      milesToKilometers,
      statisticProblems,
      popularMovies,
      friendliTCache,
      addNumbers,
      graphPlot,
      factorialCode,
    ],
  },
  {
    ...getBasicInfo('meta-llama-3.1-70b-instruct'),
    thumbnail: meta_llama_3_1_70b_instruct,
    companyLogo: metaLogo,
    type: ServerlessModelType.Chat,

    maxTokenThreshold: commonMaxTokenThreshold,
    initialValues: commonInitialValues,

    info: {
      id: 'meta-llama-3.1-70b-instruct',
      modelCard: {
        label: 'Meta-Llama-3.1-70B-Instruct',
        link: `${HuggingFaceUrls.Default}/meta-llama/Meta-Llama-3.1-70B-Instruct`,
      },
      license: {
        label: i18n.t('shared.license'),
        link: 'https://llama.meta.com/llama3_1/license',
      },
      policy: {
        label: i18n.t('shared.policy'),
        link: 'https://llama.meta.com/llama3_1/use-policy',
      },
    },

    examples: [
      createRecipe,
      explainNpAndP,
      funnyMathProfessor,
      compoundInterest,
      dateCalculation,
      weirdestFood,
      friendliModelOptimizer,
      generateAndCalculate,
      monteCarlo,
      mortgage,
      addFibonacciSequence,
      averageShots,
    ],
  },
  {
    ...getBasicInfo('mixtral-8x7b-instruct-v0-1'),
    thumbnail: mixtral_8x7b_instruct_v0_1,
    companyLogo: mixtralLogo,
    type: ServerlessModelType.Chat,

    maxTokenThreshold: commonMaxTokenThreshold,
    initialValues: commonInitialValues,

    info: {
      id: 'mixtral-8x7b-instruct-v0-1',
      modelCard: {
        label: 'Mixtral-8x7B',
        link: `${HuggingFaceUrls.Default}/mistralai/Mixtral-8x7B-Instruct-v0.1`,
      },
      license: {
        label: i18n.t('shared.license'),
        link: 'https://www.apache.org/licenses/LICENSE-2.0',
      },
      policy: {
        label: i18n.t('shared.policy'),
        link: 'https://mistral.ai/terms-of-use/',
      },
    },

    examples: [
      explainingTransformer,
      chatWithFriend,
      typescriptFunction,
      quadraticExpression,
      independenceDay,
      bestSneakers,
      friendliAgents,
      bubbleSort,
      threeDimensionPlot,
      exchangeRate,
      productPrimes,
      llamaPaperRelease,
    ],
  },
];

export const defaultChatModelId = 'meta-llama-3.1-70b-instruct';

export const defaultChatModelExamples = [
  draftCoverLetter,
  createRecipe,
  computeDerivativeOfFunction,
  solvePythonProblem,
  explainNpAndP,
  poemGenerator,
  funnyMathProfessor,
  explainingTransformer,
  chatWithFriend,
  typescriptFunction,
  pythonFunction,
];
